declare const __brand: unique symbol;
type Brand<T, B> = T & { readonly [__brand]: B };

export type UserId = Brand<string, 'UserId'>;
export type TenantId = Brand<string, 'TenantId'>;
export type SiteId = Brand<string, 'SiteId'>;
export type TeamMemberId = Brand<string, 'TeamMemberId'>;
export type GhostUserId = Brand<string, 'GhostUserId'>;

// Helper to create branded IDs (use when receiving from API)
export const asUserId = (id: string): UserId => id as UserId;
export const asTenantId = (id: string): TenantId => id as TenantId;
export const asSiteId = (id: string): SiteId => id as SiteId;
export const asTeamMemberId = (id: string): TeamMemberId => id as TeamMemberId;
export const asGhostUserId = (id: string): GhostUserId => id as GhostUserId;

export type UserStatus = 'active' | 'suspended' | 'deleted';
export type UserRole = 'admin' | 'superadmin' | 'staff';

export interface User {
    id: UserId;
    email: string;
    name: string | null;
    status: UserStatus;
    role: UserRole;
    email_verified_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface AuthResponse {
    user: User;
    accessToken: string;
}

export interface LoginRequest {
    email: string;
    password: string;
}

export interface RegisterRequest {
    name: string;
    email: string;
    password: string;
}

export interface ForgotPasswordRequest {
    email: string;
}

export interface ResetPasswordRequest {
    token: string;
    password: string;
}

export type SiteStatus = 'provisioning' | 'active' | 'suspended' | 'failed' | 'deleted';

// Base site fields shared across all states
interface SiteBase {
    id: SiteId;
    name: string;
    slug: string;
    custom_domain: string | null;
    url: string;
    adminUrl: string;
    iconUrl?: string | null;
    coverImageUrl?: string | null;
    created_at: string;
}

// Discriminated union: Site state determines activated_at
export type Site =
    | (SiteBase & { status: 'provisioning'; activated_at: null })
    | (SiteBase & { status: 'active'; activated_at: string })
    | (SiteBase & { status: 'suspended'; activated_at: string })
    | (SiteBase & { status: 'failed'; activated_at: null })
    | (SiteBase & { status: 'deleted'; activated_at: string | null });

export type ProvisioningStep =
    | 'validate'
    | 'create_database'
    | 'grant_permissions'
    | 'create_tables'
    | 'seed_fixtures'
    | 'seed_settings'
    | 'assign_plan'
    | 'provision_api_credentials'
    | 'activate'
    | 'send_welcome'
    | 'queue'
    | 'timeout'
    | 'lock_failed'
    | 'unknown'
    | null;

export type ProvisioningStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | null;

// Discriminated union for SiteStatus - prevents invalid combinations
export type SiteStatusResponse =
    | {
        id: SiteId;
        status: 'provisioning';
        provisioning_step: ProvisioningStep;
        provisioning_status: Exclude<ProvisioningStatus, null>;
        provisioning_error: null;
    }
    | {
        id: SiteId;
        status: 'provisioning';
        provisioning_step: ProvisioningStep;
        provisioning_status: 'failed';
        provisioning_error: string;
    }
    | {
        id: SiteId;
        status: 'active';
        provisioning_step: null;
        provisioning_status: null;
        provisioning_error: null;
    }
    | {
        id: SiteId;
        status: 'suspended';
        provisioning_step: null;
        provisioning_status: null;
        provisioning_error: null;
    }
    | {
        id: SiteId;
        status: 'failed';
        provisioning_step: ProvisioningStep;
        provisioning_status: 'failed';
        provisioning_error: string | null;
    }
    | {
        id: SiteId;
        status: 'deleted';
        provisioning_step: null;
        provisioning_status: null;
        provisioning_error: null;
    };

// Simplified type for components that don't need full discrimination
export interface SiteStatusSimple {
    id: SiteId;
    status: SiteStatus;
    provisioning_step: ProvisioningStep;
    provisioning_status: ProvisioningStatus;
    provisioning_error: string | null;
}

// Alias for backward compatibility
export type SiteStatus_Legacy = SiteStatusSimple;

export interface CreateSiteRequest {
    name: string;
    slug: string;
    timezone?: string;
}

export interface UpdateSiteRequest {
    name?: string;
    custom_domain?: string | null;
}

export interface SlugCheckResponse {
    slug: string;
    available: boolean;
    reserved?: boolean;
    error?: string;
}

export type SlugRewriteState = 'pending' | 'done';

/** Returned by POST /sites/:id/slug — the owner-facing publication address change. */
export interface SlugRenameResult {
    id: SiteId;
    slug: string;
    previous_slug: string;
    url: string;
    adminUrl: string;
    forwarding_until: string;
    url_rewrite_state: SlugRewriteState;
    cache_invalidation_state: SlugRewriteState;
    affected_connectors: string[];
    email_provider_webhook_update_required: boolean;
    email_provider_webhook_warning: string | null;
    reconciliation_needs_attention: boolean;
}

export type StripeConnectOnboardingState = 'pending' | 'onboarding' | 'active' | 'restricted' | 'disconnected';

export interface StripeConnectStatus {
    connected: boolean;
    charges_enabled: boolean;
    payouts_enabled: boolean;
    onboarding_state: StripeConnectOnboardingState;
    stripe_account_id: string | null;
    livemode: boolean;
}

export interface UserDetailSite {
    id: string;
    name: string;
    slug: string;
    status: string;
    created_at: string;
}

export interface UserDetailMembershipSite {
    tenant_id: string;
    sync_status: string;
    site_name: string;
    site_slug: string;
    site_status: string;
}

export interface UserDetailMembership {
    id: string;
    role: string;
    status: string;
    owner_id: string;
    invited_at: string;
    accepted_at: string | null;
    owner_name: string;
    owner_email: string;
    sites: UserDetailMembershipSite[];
}

export interface UserDetail extends User {
    ownedSites: UserDetailSite[];
    teamMemberships: UserDetailMembership[];
}

export interface AdminStats {
    users: {
        total: number;
    };
    sites: {
        total: number;
        active: number;
        provisioning: number;
        suspended: number;
        failed: number;
    };
}

export interface AdminSite extends SiteBase {
    status: SiteStatus;
    activated_at: string | null;
    owner_name: string;
    owner_email: string;
}

export interface PaginatedResponse<T> {
    data: T[];
    pagination: {
        page: number;
        perPage: number;
        total: number;
        totalPages: number;
    };
}

export type ApiErrorCode =
    | 'UNAUTHORIZED'
    | 'FORBIDDEN'
    | 'NOT_FOUND'
    | 'VALIDATION_ERROR'
    | 'CONFLICT'
    | 'RATE_LIMIT_EXCEEDED'
    | 'EMAIL_NOT_VERIFIED'
    | 'INTERNAL_ERROR'
    | string;

export interface ApiError {
    code: ApiErrorCode;
    message: string;
    details?: Array<{
        field: string;
        message: string;
    }>;
}

export type ApiResponse<T = unknown> =
    | { success: true; data: T; message?: string; pagination?: PaginatedResponse<unknown>['pagination'] }
    | { success: false; error: ApiError; message?: string };

export type TeamRole = 'Administrator' | 'Editor' | 'Author' | 'Contributor';
export type TeamMemberStatus = 'pending' | 'active';
export type SyncStatus = 'pending' | 'synced' | 'failed' | 'removed';
export type AccessType = 'owner' | 'team_member';

export interface TeamMemberSite {
    tenant_id: SiteId;
    site_name: string;
    site_slug: string;
    ghost_user_id: GhostUserId | null;
    sync_status: SyncStatus;
    sync_error: string | null;
}

export interface TeamMember {
    id: TeamMemberId;
    owner_id: UserId;
    email: string;
    name: string;
    role: TeamRole;
    status: TeamMemberStatus;
    platform_user_id: UserId | null;
    invited_at: string;
    created_at: string;
    updated_at: string;
    sites: TeamMemberSite[];
}

export interface InviteTeamMemberRequest {
    email: string;
    name: string;
    role: TeamRole;
    siteIds: SiteId[];
}

export interface UpdateTeamMemberRequest {
    name?: string;
    role?: TeamRole;
    siteIds?: SiteId[];
}

export interface InviteResult {
    teamMember: TeamMember;
    siteResults: Array<{
        siteId: SiteId;
        siteName: string;
        status: 'synced' | 'failed';
        error?: string;
    }>;
}

export interface InviteInfo {
    email: string;
    name: string;
    role: TeamRole;
    inviterName: string;
    alreadyHasAccount: boolean;
}

export interface AcceptInviteResponse {
    teamMemberId: TeamMemberId;
    email: string;
    requiresSignup: boolean;
    requiresLogin: boolean;
}

export type SiteWithAccess = Site & {
    access_type: AccessType;
    team_role: TeamRole | null;
};

export interface ApiToken {
    id: string;
    user_id: UserId;
    name: string;
    client_label?: string | null;
    token_prefix: string;
    scopes: SiteId[] | null;
    scopesInvalid?: boolean;
    last_used_at: string | null;
    revoked_at: string | null;
    expires_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface CreateApiTokenRequest {
    name: string;
    clientLabel?: string;
    scopes?: SiteId[] | null;
    expiresAt?: string | null;
}

export interface ApiTokenActivity {
    id: string;
    action: string;
    tool: string | null;
    outcome: string | null;
    error_code: string | null;
    site_id: SiteId | null;
    created_at: string;
}

export interface ApiTokenActivityPage {
    items: ApiTokenActivity[];
    nextCursor: string | null;
}

export interface McpConnectionTestResult {
    ok: boolean;
    code?: string;
    message?: string;
    siteCount?: number;
}

export interface CreateApiTokenResponse {
    token: string;
    apiToken: ApiToken;
}

export interface SiteCredentials {
    apiUrl: string;
    contentApiUrl: string;
    contentKey: string;
    adminKey: {
        status: 'active' | 'revoked';
        masked: string | null;
        createdAt: string | null;
        updatedAt: string | null;
    };
}

export interface RevealedSiteCredential {
    key: string;
    adminKey?: SiteCredentials['adminKey'];
}

export function isProvisioningSite(site: Site): site is Site & { status: 'provisioning' } {
    return site.status === 'provisioning';
}

export function isActiveSite(site: Site): site is Site & { status: 'active' } {
    return site.status === 'active';
}

export function isFailedSite(site: Site): site is Site & { status: 'failed' } {
    return site.status === 'failed';
}

export function isSuspendedSite(site: Site): site is Site & { status: 'suspended' } {
    return site.status === 'suspended';
}

// ============================================================================
// Platform Plans Types
// ============================================================================

export type PlanId = Brand<string, 'PlanId'>;
export const asPlanId = (id: string): PlanId => id as PlanId;

export type BillingPeriod = 'monthly' | 'yearly' | 'lifetime';
export type SubscriptionStatus = 'pending' | 'active' | 'past_due' | 'cancelled' | 'refunded' | 'failed';
export type InvoiceStatus = 'pending' | 'paid' | 'failed' | 'refunded';

export interface PlanLimit {
    id: string;
    plan_id: PlanId;
    limit_key: string;
    limit_value: number;
    error_message: string | null;
    upgrade_prompt: string | null;
}

export interface PlanFeature {
    id: string;
    plan_id: PlanId;
    feature_key: string;
    is_enabled: boolean;
    feature_value: string | null;
}

export interface PlatformPlan {
    id: PlanId;
    name: string;
    slug: string;
    description: string | null;
    price_monthly_cents: number;
    price_yearly_cents: number;
    price_lifetime_cents: number;
    is_featured: boolean;
    sort_order: number;
    badge_text: string | null;
    is_active: boolean;
    is_public: boolean;
    paykickstart_product_id_monthly: string | null;
    paykickstart_product_id_yearly: string | null;
    paykickstart_product_id_lifetime: string | null;
    limits: PlanLimit[];
    features: PlanFeature[];
    created_at: string;
    updated_at: string;
}

export interface PlanStats {
    id: PlanId;
    name: string;
    slug: string;
    price_monthly_cents: number;
    price_yearly_cents: number;
    price_lifetime_cents: number;
    site_count: number;
    is_active: boolean;
}

export interface UsageItem {
    used: number;
    limit: number;
    percent: number;
    isOver: boolean;
}

export interface UsageData {
    staff: UsageItem;
    members: UsageItem;
    newsletters: UsageItem;
    storage_mb: UsageItem;
    email_sends_monthly: UsageItem;
}

export interface Subscription {
    id: string;
    platform_user_id: UserId;
    tenant_id: SiteId;
    plan_id: PlanId;
    billing_period: BillingPeriod;
    amount_cents: number;
    currency: string;
    status: SubscriptionStatus;
    starts_at: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    cancelled_at: string | null;
    scheduled_plan_id: PlanId | null;
    grace_period_end: string | null;
    created_at: string;
    updated_at: string;
}

export interface BillingInfo {
    tenant_id: SiteId;
    site_name: string;
    site_slug: string;
    plan_id: PlanId | null;
    subscription: Subscription | null;
}

export interface SiteUsageData {
    complimentary?: {plan_name: string; expires_at: string | null} | null;
    overrides?: {kind: string; key: string; value: string | number | boolean; expires_at: string | null}[];
    tenant_id: SiteId;
    site_name: string;
    site_slug: string;
    plan_id: PlanId | null;
    usage: UsageData;
    features: Record<string, boolean>;
}

export interface SiteUsageDetail {
    plan: {
        id: PlanId;
        name: string;
        billing_period: BillingPeriod | null;
    } | null;
    usage: UsageData;
    features: Record<string, boolean>;
}

export interface Invoice {
    id: string;
    subscription_id: string | null;
    platform_user_id: UserId;
    tenant_id: SiteId | null;
    plan_id: PlanId | null;
    invoice_number: string;
    amount_cents: number;
    currency: string;
    status: InvoiceStatus;
    billing_period: BillingPeriod | null;
    description: string | null;
    site_name?: string;
    paid_at: string | null;
    due_date: string | null;
    period_start: string | null;
    period_end: string | null;
    created_at: string;
}

export interface CreatePlanRequest {
    name: string;
    slug: string;
    description?: string;
    price_monthly_cents?: number;
    price_yearly_cents?: number;
    price_lifetime_cents?: number;
    is_featured?: boolean;
    sort_order?: number;
    badge_text?: string;
    is_active?: boolean;
    is_public?: boolean;
    paykickstart_product_id_monthly?: string;
    paykickstart_product_id_yearly?: string;
    paykickstart_product_id_lifetime?: string;
    limits?: Record<string, { value: number; error?: string }>;
    features?: Record<string, boolean>;
}

export type UpdatePlanRequest = Partial<CreatePlanRequest>;

export interface DowngradeValidation {
    canDowngrade: boolean;
    violations: Array<{
        limit: string;
        current: number;
        allowed: number;
        blocking: boolean;
        message: string;
    }>;
    blockingViolations: Array<{
        limit: string;
        message: string;
    }>;
}

export interface ProrationData {
    current_plan: {
        id: PlanId;
        name: string;
        price_cents: number;
    };
    new_plan: {
        id: PlanId;
        name: string;
        price_cents: number;
    };
    billing_period: BillingPeriod;
    days_remaining: number;
    total_days: number;
    credit_cents: number;
    charge_cents: number;
    net_cents: number;
}

export interface PricingPlan {
    id: PlanId;
    name: string;
    slug: string;
    description: string | null;
    badge: string | null;
    is_featured: boolean;
    pricing: {
        monthly: { amount: number; currency: string; checkout_url?: string | null } | null;
        yearly: { amount: number; currency: string; savings: string | null; checkout_url?: string | null } | null;
        lifetime: { amount: number; currency: string; checkout_url?: string | null } | null;
    };
    limits: Record<string, number | string>;
    features: string[];
}

export interface PricingData {
    plans: PricingPlan[];
}
